import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { reasonsToConsult, consultationInfo } from '../mockData';
import { Card, CardContent } from './ui/card';
import { Clock, Euro, Info } from 'lucide-react';
import { Button } from './ui/button';
import { createStaggerContainer, fadeInLeft, fadeInRight, fadeInUp, getScrollAnimationProps, scaleIn } from '../lib/animations';

const ConsultationsSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const scrollAnimationProps = getScrollAnimationProps(shouldReduceMotion, { amount: 0.25 });
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.16, delayChildren: 0.1 });

  return (
    <motion.section id="consultations" className="py-12 sm:py-16 lg:py-20 bg-[#E5DDD0]" {...scrollAnimationProps} variants={containerVariants}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-8 sm:mb-12" variants={createStaggerContainer({ staggerChildren: 0.12 })}>
          <motion.h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#5A5045] mb-3 sm:mb-4" variants={fadeInUp}>
            Mes Consultations
          </motion.h2>
          <motion.p className="text-sm sm:text-base lg:text-lg text-[#6B5E54] max-w-3xl mx-auto px-4" variants={fadeInUp}>
            Des prestations adaptées à vos besoins spécifiques pour vous accompagner vers un bien-être durable.
          </motion.p>
        </motion.div>

        <motion.div className="bg-white/60 p-6 sm:p-8 lg:p-10 rounded-sm border-l-4 border-[#7D8471] mb-8 sm:mb-12" variants={fadeInUp}>
          <motion.h3 className="text-2xl sm:text-3xl font-serif text-[#5A5045] mb-4 sm:mb-6" variants={fadeInUp}>
            Pour quelles raisons consulter ?
          </motion.h3>
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6" variants={createStaggerContainer({ staggerChildren: 0.08 })}>
            {reasonsToConsult.map((reason, index) => (
              <motion.div key={index} className="flex items-center space-x-2" variants={fadeInLeft}>
                <div className="w-2 h-2 bg-[#7D8471] rounded-full flex-shrink-0"></div>
                <span className="text-xs sm:text-sm text-[#5A5045]">{reason}</span>
              </motion.div>
            ))}
          </motion.div>
          <motion.p className="text-xs sm:text-sm text-[#6B5E54] italic" variants={fadeInUp}>
            Cette liste n'est pas exhaustive. N'hésitez pas à me contacter pour discuter de votre situation spécifique et déterminer si mon approche peut vous aider.
          </motion.p>
        </motion.div>

        <motion.div className="grid sm:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12" variants={createStaggerContainer({ staggerChildren: 0.14 })}>
          <motion.div variants={scaleIn} whileHover={shouldReduceMotion ? undefined : { y: -6, boxShadow: '0 20px 40px rgba(125, 132, 113, 0.2)' }}>
            <Card className="border-none shadow-lg bg-white rounded-sm">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#7D8471]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#7D8471]" size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-serif text-[#5A5045] mb-2">Durée</h4>
                    <p className="text-sm sm:text-base text-[#6B5E54]">{consultationInfo.duration}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={scaleIn} whileHover={shouldReduceMotion ? undefined : { y: -6, boxShadow: '0 20px 40px rgba(125, 132, 113, 0.2)' }}>
            <Card className="border-none shadow-lg bg-white rounded-sm">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#7D8471]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Euro className="text-[#7D8471]" size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-serif text-[#5A5045] mb-2">Tarif</h4>
                    <p className="text-sm sm:text-base text-[#6B5E54]">{consultationInfo.price}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        <motion.div className="bg-[#7D8471]/5 p-6 sm:p-8 rounded-sm border border-[#7D8471]/20 mb-8" variants={fadeInRight}>
          <div className="flex items-start space-x-3 sm:space-x-4">
            <Info className="text-[#7D8471] flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-base sm:text-lg font-semibold text-[#5A5045] mb-2">Préparation</h4>
              <p className="text-xs sm:text-sm text-[#6B5E54] leading-relaxed">{consultationInfo.preparation}</p>
            </div>
          </div>
        </motion.div>

        <motion.div className="text-center bg-white/80 p-6 sm:p-8 rounded-sm" variants={fadeInUp}>
          <motion.p className="text-sm sm:text-base text-[#6B5E54] mb-4 sm:mb-6 font-medium" variants={fadeInUp}>
            {consultationInfo.note}
          </motion.p>
          <motion.a
            href="#contact"
            whileHover={shouldReduceMotion ? undefined : { y: -3 }}
          >
            <Button className="bg-[#7D8471] hover:bg-[#6B7361] text-white px-8 sm:px-10 py-5 sm:py-6 text-sm sm:text-base rounded-sm transition-all duration-300 hover:shadow-xl">
              Prendre rendez-vous
            </Button>
          </motion.a>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ConsultationsSection;